import React, { Component } from "react";
import { Col, Table, Row, notification } from "antd";
import { ContentWrapper, Icon } from "components";
import { Helmet } from "react-helmet";
import { connect } from "react-redux";
import { delayedDispatch, setBreadcrumb, setLoader, setTitle, updateCrumb } from "store/actions";
import { API, Endpoints } from "utils/api";
import strings from "utils/strings";
import { formatPrice, getSuccessLevelBadge, xlsxSettings } from "utils/utils";
import xlsx from "json-as-xlsx";
import moment from "moment";
import { Area } from "@ant-design/charts";
import { DateTime } from "luxon";
import { getColumnFilter } from "utils/comp-utils";

class Special extends Component<any, any> {
	searchTimeout: NodeJS.Timeout | undefined;

	constructor(props: any) {
		super(props);

		this.state = {
			partner: undefined,
			partnerOptions: [],
			partnerSearch: "",
			startDate: moment().add({ months: -5 }).startOf("month"),
			endDate: moment().endOf("month"),
			partnerDetail: null,
			eligible: false,
			commissionsChart: [],
			commissions: []
		};
	}

	async componentDidMount(): Promise<void> {
		const { dispatch } = this.props;

		dispatch(setTitle(""));

		await this.getData();
		this.breadcrumb();
	}

	componentDidUpdate(): void {
		const { dispatch } = this.props;
		dispatch(updateCrumb());
	}

	async getData() {
		const { partner, startDate, endDate } = this.state;
		const { dispatch, countries } = this.props;

		dispatch(setLoader(true));

		const body = {
			type: "special",
			partner,
			startDate: startDate.toISOString(),
			endDate: endDate.toISOString()
		};

		try {
			const response = await API.post({
				url: Endpoints.uriCommissions("search-by-type"),
				data: body
			});

			if (response.ok) {
				const { partner, eligible = false, settings, commissionsChart, commissions, cards } = response.data.results;

				const commissionsParsed = commissions.map((entry: any) => {
					const countryCallingCodePartner = entry.partner.phone?.substring(0, 4).replace("+", "");
					let countryPartner;

					if (countryCallingCodePartner) {
						const countryObj = countries.find((elem: any) => elem.callingCodes.includes(countryCallingCodePartner));
						countryPartner = {
							name: countryObj?.name,
							alpha2Code: countryObj?.alpha2Code
						};
					}

					return {
						...entry,
						partner: { ...entry.partner, country: countryPartner }
					};
				});

				this.setState({
					partnerDetail: partner,
					eligible,
					settings,
					commissionsChart,
					commissions: commissionsParsed,
					cards
				});
			} else {
				notification.error({
					message: strings.commissions.special,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	handleSearch(partner: any) {
		this.setState({ partnerSearch: partner });

		if (partner.length < 3) return;

		if (this.searchTimeout) clearTimeout(this.searchTimeout);

		this.searchTimeout = setTimeout(async () => {
			this.getUsers();
		}, 500);
	}

	handleSelect = (dates: any) => {
		const newDates = [];
		if (dates && dates[0]) {
			const start = moment(dates[0]);
			newDates.push(start.startOf("month"));
		}

		if (dates && dates[1]) {
			const end = moment(dates[1]);
			newDates.push(end.endOf("month"));
		}

		if (newDates[0] && newDates[1] && newDates[0].isSame(newDates[1], "month")) {
			newDates[0] = moment(newDates[0]).startOf("month");
			newDates[1] = moment(newDates[1]).endOf("month");
		}

		this.setState(
			{
				startDate: newDates[0],
				endDate: newDates[1]
			},
			() => this.getData()
		);
	};

	async getUsers() {
		const { partnerSearch } = this.state;

		try {
			const body = { search: partnerSearch, page: 0, perPage: 10 };

			const response = await API.post({
				url: Endpoints.uriPartners("search"),
				data: body
			});

			if (response.ok) {
				const { users = [] } = response.data.results || {};
				this.setState({ partnerOptions: users });
			} else {
				notification.error({
					message: strings.commissions.special,
					description: response?.data?.message || strings.serverErrors.wentWrong,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.serverErrors.title,
				description: err as string,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	breadcrumb() {
		delayedDispatch(
			setBreadcrumb(() => {
				const { startDate, endDate, partnerOptions } = this.state;

				return {
					locations: [
						{
							text: strings.commissions.special,
							icon: "organization"
						}
					],
					actions: [
						{
							type: "select",
							text: strings.fields.partner,
							placeholder: strings.generic.searchByEmailOrName,
							options: partnerOptions.map((partner: any) => ({
								value: partner._id,
								text: `${partner.name} (${partner.email})`
							})),
							onChange: (partner: any) => {
								this.setState({ partner }, () => this.getData());
							},
							value: this.state.partner,
							showSearch: true,
							onSearch: (value: string) => this.handleSearch(value),
							style: { width: 300, height: 40 },
							allowClear: true,
							separator: "right"
						},
						{
							type: "datePicker",
							text: strings.dashboard.period,
							dates: [startDate, endDate],
							monthly: true,
							onChange: this.handleSelect,
							className: "fixedPicker"
						}
					]
				};
			})
		);
	}

	exportData = () => {
		const { commissions: exportData = [] } = this.state;

		const columns = [
			{
				label: "Data",
				value: (row: any) => (row.date ? DateTime.fromISO(row.date).toFormat("dd/MM/yyyy HH:mm") : "")
			},
			{
				label: "País",
				value: (row: any) => row.partner?.country?.name || ""
			},
			{
				label: "Parceiro",
				value: (row: any) =>
					row.partner?.name ? `${row.partner.name}${row.partner?.userCode ? ` (#${row.partner.userCode})` : ""}` : ""
			},
			{
				label: "Nível de Sucesso (Na Data)", // Ranking at the time
				value: (row: any) => getSuccessLevelBadge(row.actualSuccessLevel) || "-"
			},
			{
				label: "Bónus",
				value: (row: any) => getSuccessLevelBadge(row.rankTag) || "-"
			},
			{
				label: "Valor a Receber",
				value: (row: any) => formatPrice(row.totalValueToReceive || 0)
			},
			{
				label: "Nº Parcela",
				value: (row: any) => row.installmentNumber || 0
			},
			{
				label: "Valor Parcela",
				value: (row: any) => formatPrice(row.value || 0)
			},
			{
				label: "Valor a Receber Restante",
				value: (row: any) => formatPrice(row.valueLeftToReceive || 0)
			},
			{
				label: "Validade",
				value: (row: any) => (row.expirationDate ? DateTime.fromISO(row.expirationDate).toFormat("dd/MM/yyyy HH:mm") : "")
			}
		];

		const data = [
			{
				sheet: "Recompensa Especial",
				columns,
				content: exportData
			}
		];

		xlsx(data, xlsxSettings("Recompensa Especial"));
	};

	renderChart() {
		const { commissionsChart = [] } = this.state;

		const config = {
			data: commissionsChart,
			xField: "month",
			yField: "special",
			appendPadding: 10,
			smooth: true,
			animation: {
				appear: {
					animation: "path-in",
					duration: 0
				}
			},
			meta: {
				special: {
					alias: strings.wallet.totalBilled
				}
			},
			tooltip: {
				formatter: (datum: any) => {
					return { name: strings.wallet.totalBilled, value: formatPrice(datum.special) };
				}
			}
		};

		return (
			<ContentWrapper extraStyle={{ marginBottom: 0 }}>
				<div className="ScreenHeader">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.wallet.monthlyControl}</h2>
					</div>
				</div>
				<Area style={{ marginTop: 20 }} {...config} />
			</ContentWrapper>
		);
	}

	renderMovements() {
		const { commissions = [] } = this.state;

		return (
			<ContentWrapper>
				<div className="ScreenHeader --multi">
					<div className="ScreenHeaderLeft">
						<Icon name="diagram" />
						<h2>{strings.commissions.special}</h2>
					</div>
					<div className="ScreenHeaderRight">
						<p>
							{strings.formatString(
								strings.wallet.earnedPeriod,
								formatPrice(commissions.reduce((acc: number, entry: any) => acc + entry.value, 0))
							)}
						</p>

						<button className="EthicAddButton" onClick={this.exportData}>
							<Icon name="print" />
						</button>
					</div>
				</div>
				<Table
					style={{ marginTop: 20 }}
					scroll={{ x: "max-content" }}
					columns={[
						{
							title: strings.fields.date,
							dataIndex: "date",
							key: "date",
							render: (date: string) => DateTime.fromISO(date).toFormat("dd/MM/yyyy HH:mm"),
							defaultSortOrder: "descend",
							sorter: (a, b) => DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis(),
							width: 150
						},
						{
							title: strings.fields.country,
							dataIndex: "partner",
							key: "partnerCountry",
							sorter: (a: any, b: any) => {
								return a?.country?.name.localeCompare(b?.country?.name);
							},
							render: (partner: any) => {
								return (
									<span className="TableRowAligned">
										<div className="react-tel-input">
											<div className="selected-flag">
												<span className={`flag ${partner?.country?.alpha2Code?.toLowerCase()}`} />
											</div>
										</div>
										{partner?.country?.name}
									</span>
								);
							},
							...getColumnFilter(["partner.country.name", "partner.country.alpha2Code"])
						},
						{
							title: strings.fields.partner,
							dataIndex: "partner",
							key: "name",
							render: (partner: any) => {
								return (
									<span className="TableRowAligned">
										{partner.name}
										{Boolean(partner?.userCode) ? ` (#${partner.userCode})` : ""}
									</span>
								);
							},
							sorter: (a, b) => a.name?.localeCompare(b.name),
							...getColumnFilter(["partner.name", "partner.userCode", "partner.email"])
						},
						{
							title: strings.users.partnerRankingAtTheTime,
							dataIndex: "actualSuccessLevel",
							key: "actualSuccessLevel",
							render: (_: any, record: any) => getSuccessLevelBadge(record?.actualSuccessLevel) || "-",
							sorter: (a, b) => a?.actualSuccessLevel?.localeCompare(b?.actualSuccessLevel),
							...getColumnFilter(["actualSuccessLevel"])
						},
						{
							title: strings.generic.bonus,
							dataIndex: "rankTag",
							key: "rankTag",
							render: (_: any, record: any) => getSuccessLevelBadge(record?.rankTag) || "-",
							sorter: (a, b) => a?.rankTag?.localeCompare(b?.rankTag),
							...getColumnFilter(["rankTag"])
						},
						{
							title: strings.commissions.valueToReceive,
							dataIndex: "totalValueToReceive",
							key: "totalValueToReceive",
							render: (totalValueToReceive: number) => formatPrice(totalValueToReceive || 0),
							sorter: (a, b) => a.totalValueToReceive - b.totalValueToReceive
						},
						{
							title: strings.commissions.installmentNumber,
							dataIndex: "installmentNumber",
							key: "installmentNumber",
							render: (installmentNumber: number) => installmentNumber || 0,
							sorter: (a, b) => a.installmentNumber - b.installmentNumber
						},
						{
							title: strings.commissions.installmentAmount,
							dataIndex: "value",
							key: "value",
							render: (value: number) => formatPrice(value || 0),
							sorter: (a, b) => a.value - b.value
						},
						{
							title: strings.commissions.installmentValueLeft,
							dataIndex: "valueLeftToReceive",
							key: "valueLeftToReceive",
							render: (valueLeftToReceive: number) => formatPrice(valueLeftToReceive || 0),
							sorter: (a, b) => a.valueLeftToReceive - b.valueLeftToReceive
						},
						{
							title: strings.generic.expirationDate,
							dataIndex: "expirationDate",
							key: "expirationDate",
							render: (expirationDate: string) => DateTime.fromISO(expirationDate).toFormat("dd/MM/yyyy HH:mm"),
							defaultSortOrder: "descend",
							sorter: (a, b) => DateTime.fromISO(a.expirationDate).toMillis() - DateTime.fromISO(b.expirationDate).toMillis(),
							width: 150
						}
					]}
					dataSource={commissions}
					rowKey={(record) => record._id}
				/>
			</ContentWrapper>
		);
	}

	renderPartner() {
		const { partner, partnerDetail, eligible, settings } = this.state;
		const { bonuses } = settings || {};
		const { special } = bonuses || {};

		if (!partner || !special) return null;

		return (
			<Row gutter={[20, 20]}>
				{!eligible && (
					<Col xs={24}>
						<ContentWrapper>
							<div className="ScreenHeader">
								<div className="ScreenHeaderLeft">
									<Icon name="user" />
									<h2>{strings.commissions.eligibility}</h2>
								</div>
							</div>
							<p>
								<strong>
									{strings.commissions.actualRank}: {getSuccessLevelBadge(partnerDetail?.partner?.successLevel)}
								</strong>
							</p>
							<p className="CommissionsPartnersAttention">{strings.commissions.notEligibleSpecial}</p>
						</ContentWrapper>
					</Col>
				)}
				<Col xs={24}>
					<ContentWrapper>
						<div className="ScreenHeader">
							<div className="ScreenHeaderLeft">
								<Icon name="points" />
								<h2>{strings.fields.description}</h2>
							</div>
						</div>
						<p className="CommissionsPartnersAttention">{strings.commissions.specialDescription}</p>
					</ContentWrapper>
				</Col>
				<Col xs={24}>
					<ContentWrapper>
						<div className="ScreenHeader">
							<div className="ScreenHeaderLeft">
								<Icon name="points" />
								<h2>{strings.commissions.special}</h2>
							</div>
						</div>
						<Row>
							<Col xs={24} md={12}>
								<table className="CommissionTpvTable">
									<thead>
										<tr>
											<th>{strings.settings.successLevel}</th>
											<th>{strings.generic.bonus}</th>
										</tr>
									</thead>
									<tbody>
										{Object.entries(special)?.map((entry: any, index: number) => {
											const rank = partnerDetail?.partner?.rewards?.[entry[0]];
											const reached = rank?.achieved;

											const style: React.CSSProperties = {};
											if (reached) {
												style.backgroundColor = "#00b5b033";
												style.fontWeight = 600;
												style.color = "#00b5b0";
											}

											return (
												<tr key={index} style={style}>
													<td>{getSuccessLevelBadge(entry[0])}</td>
													<td>{formatPrice(entry[1])}</td>
												</tr>
											);
										})}
									</tbody>
								</table>
							</Col>
						</Row>
					</ContentWrapper>
				</Col>
			</Row>
		);
	}

	render() {
		const { partner, cards, eligible, partnerDetail } = this.state;
		const { available = 0, spent = 0, expired = 0, total = 0 } = cards || {};

		const CARDS = [
			{
				icon: "wallet",
				value: total,
				description: partner ? strings.commissions.earnedFromRepass : strings.commissions.totalRepassToThisDay,
				type: "currency"
			},
			{
				icon: "wallet",
				value: available,
				description: strings.wallet.available,
				type: "currency"
			},
			{
				icon: "wallet",
				value: spent,
				description: strings.wallet.spent,
				type: "currency"
			},
			{
				icon: "wallet",
				value: expired,
				description: strings.wallet.expired,
				type: "currency"
			}
		];

		return (
			<div className="CommissionSpecial">
				<Helmet>
					<title>{strings.commissions.special}</title>
					<meta name="description" content="Description of special commissions" />
				</Helmet>
				{Boolean(partner) && !eligible && (
					<Col xs={24}>
						<ContentWrapper extraStyle={{ backgroundColor: "#dce0dd" }}>
							<div className="ScreenHeader">
								<div className="ScreenHeaderLeft">
									<Icon name="user" />
									<h2>{strings.commissions.eligibility}</h2>
								</div>
							</div>
							<p>
								<strong>
									{strings.commissions.actualRank}: {getSuccessLevelBadge(partnerDetail?.partner?.successLevel)}
								</strong>
							</p>
							<p className="CommissionsPartnersAttention">{strings.commissions.notEligibleBronze}</p>
						</ContentWrapper>
					</Col>
				)}
				<div className="IronCards">
					<Row gutter={[20, 20]}>
						{CARDS.map((card, index) => (
							<Col xs={24} md={6} key={index}>
								<div className="IconCardOuterContainer">
									<div className="IconCard">
										<div className="IconCardHeader">
											<div className="IconCardHeaderMask" />
											<div className="IconCardType">
												<Icon name={card.icon} />
											</div>
										</div>
										<div className="IconCardBody">
											<span className="IconCardValue">
												{card.type === "currency" ? formatPrice(card.value) : card.value}
											</span>
											<h3 className="IconCardDescription">{card.description}</h3>
										</div>
									</div>
								</div>
							</Col>
						))}
						<Col xs={24}>{this.renderChart()}</Col>
						<Col xs={24}>{this.renderMovements()}</Col>
						<Col xs={24}>{Boolean(partner) && this.renderPartner()}</Col>
					</Row>
				</div>
			</div>
		);
	}
}

const mapStateToProps = (state: any) => ({
	language: state.language,
	countries: state.countries
});

export default connect(mapStateToProps)(Special);
